import { useRef, useState } from "react";
import {
  type User,
  type WorkflowStepUser,
  WorkflowStepUserPermissions,
  WorkflowStepUserResponses,
} from "~/api/types";
import { Button } from "~/atoms/button";
import useUsers from "~/api/swr/useUsers";
import { PlatformRole, UserStatus } from "~/config/types";
import { printFullName } from "~/helpers/user";
import { includesSearch } from "~/helpers/string";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/atoms/command";
import { ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "~/atoms/popover";
import AvatarWithFallback from "./AvatarWithFallback";
import { useTranslation } from "react-i18next";
import { useUserContext } from "~/hooks/useUserContext";

/** Which contributors may be assigned to a workflow step. Coordinators and
 * superadmins are always assignable. */
export type ContributorScope =
  | { kind: "all" } // templates: any contributor
  | { kind: "category"; categoryId: number } // document with a category
  | { kind: "none" }; // document without a category

const isContributorInScope = (
  user: Pick<User, "categories">,
  scope: ContributorScope,
): boolean => {
  switch (scope.kind) {
    case "all":
      return true;
    case "category":
      return user.categories.some(
        (category) => category.id === scope.categoryId,
      );
    case "none":
      return false;
  }
};

type UserFormProps = {
  selectedUsers: WorkflowStepUser[];
  handleAddUser: (userInput: WorkflowStepUser) => void;
  contributorScope: ContributorScope;
};

export function UserForm({
  selectedUsers,
  handleAddUser,
  contributorScope,
}: UserFormProps) {
  const { t } = useTranslation();
  const { user: currentUser } = useUserContext();

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: users } = useUsers({
    status: [UserStatus.active, UserStatus.pending],
    roles: [
      PlatformRole.superAdmin,
      PlatformRole.coordinator,
      PlatformRole.contributor,
    ],
    putCurrentUserAtTop: true,
  });

  const filteredUsers = users?.filter(
    (user) =>
      !selectedUsers.some((selectedUser) => selectedUser.userId === user.id) &&
      (user.role === PlatformRole.superAdmin ||
        user.role === PlatformRole.coordinator ||
        (user.role === PlatformRole.contributor &&
          isContributorInScope(user, contributorScope))) &&
      (includesSearch(printFullName(user), searchValue) ||
        includesSearch(user.email, searchValue)),
  );

  const handleSubmit = (user: User) => {
    handleAddUser({
      userId: user.id,
      firstName: user.firstName.trim(),
      lastName: user.lastName?.trim(),
      imageUrl: user.imageUrl,
      permission: WorkflowStepUserPermissions.write,
      responseStatus: WorkflowStepUserResponses.pending,
    });

    setSearchValue("");
  };

  if (filteredUsers === undefined) {
    return null;
  }

  return (
    <div className="flex gap-2">
      <div className="grow">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="h-auto min-h-10 w-full justify-between py-2"
            >
              <div className="flex w-full flex-wrap gap-1">
                <span className="text-muted-foreground">
                  {t(($) => $.commons.workflow.addUser)}
                </span>
              </div>
              <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0"
            style={{ width: "var(--radix-popover-trigger-width)" }}
            align="start"
          >
            <Command shouldFilter={false}>
              <CommandInput
                ref={inputRef}
                placeholder="Search..."
                value={searchValue}
                onValueChange={setSearchValue}
                className="h-9"
              />
              <CommandList>
                {filteredUsers.length > 0 ? (
                  <CommandGroup>
                    {filteredUsers.map((user) => (
                      <CommandItem
                        key={user.id}
                        value={`${user.id}`}
                        onSelect={() => {
                          handleSubmit(user);
                          setOpen(false);
                        }}
                        className="flex items-center gap-2"
                      >
                        <AvatarWithFallback user={user} size="xs" />
                        <div className="flex flex-col">
                          <span>
                            {printFullName(user, user.id === currentUser.id)}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {user.email}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : (
                  <CommandEmpty>
                    {searchValue ? "No users found." : "No users available."}
                  </CommandEmpty>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
